import { useCallback, useEffect, useRef, useState } from 'react';
import './GoldenQuest.css';
import { countdownLabel, fmtDay, fmtDayLong, fmtDayOrdinal, fmtMonDayOrdinal } from './campaign';
import {
  fetchGoldenQuest, markStorySeen, preRegister, setReminder,
  markLiveIntroSeen, markChestCelebrated, openGem as openGemApi, mapGems,
  saveSurveyProgress, submitSurvey as submitSurveyApi, surveyChestFrom,
  trackSurveyStarted, trackSurveyAnswer,
} from './api';
import {
  goHome, shareWithFriends, loadCard, offersBrands, lumpsumScreen,
  getSessionToken, listenForSession,
} from '../Helper/native';
import Story from './components/Story';
import PreReg from './components/PreReg';
import GoLive from './components/GoLive';
import Celebrate from './components/Celebrate';
import Rewards from './components/Rewards';
import Hero from './components/Hero';
import StreakCard from './components/StreakCard';
import GrandPrizes from './components/GrandPrizes';
import LinksSection from './components/LinksSection';
import ShareCard from './components/ShareCard';
import StickyBar from './components/StickyBar';
import Toast from './components/Toast';
import SurveyCard from './components/SurveyCard';
import Survey from './components/Survey';
import QuestLoader, { LOADER_EXIT_MS } from './components/QuestLoader';
import { PrizesList, TermsList, WinnersList } from './components/Sheets';
import { Lantern, SectionTitle, Sheet, TickerBand, SHEET_EXIT_MS } from './components/Shared';
import { prefetchLottie, loadHubPrizes } from './components/Lottie';

/* The design is laid out on a fixed 375px stage (every offset in the CSS is
   absolute). Scale the stage to the device width with CSS `zoom`, capped so
   tablets / desktop get a centred phone-sized column instead of a giant one. */
const STAGE_W = 375;
const STAGE_MAX_W = 430;
const measureStage = () => {
  const zoom = Math.min(window.innerWidth || STAGE_W, STAGE_MAX_W) / STAGE_W;
  /* viewport height in stage px — the full-screen overlays are sized with it */
  return { zoom, stageHeight: Math.round((window.innerHeight || 812) / zoom) };
};

function useStage() {
  const [stage, setStage] = useState(measureStage);
  useEffect(() => {
    const onResize = () => setStage(measureStage());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return stage;
}

/* the body sheet rides up over the hero by this much (.gq-body margin-top) */
const BODY_OVERLAP = 26;
const TICKER_H = 40;

/* Hero dims and slides down at a third of the scroll as the streak sheet
   rides over it, and the ticker pins to the top once the body's own ticker
   band reaches it (prototype heroScroll). The slide is capped at `lagMax`
   px: the sheet gains on "View all prizes" by exactly the lag, so on stages
   where the button starts under the footer (see revealFit) the cap keeps the
   sheet from covering it before it has been scrolled clear. Styles are
   written straight to the nodes so scrolling doesn't re-render. */
function useHeroScroll(heroRef, tickRef, zoom, lagMax) {
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const hero = heroRef.current;
      if (!hero) return;
      const y = window.scrollY / zoom; // document scroll is unzoomed; the hero's px are zoomed
      const k = Math.min(y / 380, 1);
      hero.style.opacity = (1 - k * 0.62).toFixed(3);
      hero.style.transform = `translate3d(0,${Math.min(y * 0.32, lagMax).toFixed(1)}px,0)`;
      const tick = tickRef.current;
      if (tick) tick.classList.toggle('gq-show', y >= hero.offsetHeight - BODY_OVERLAP - TICKER_H);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [heroRef, tickRef, zoom, lagMax]);
}

/* With the footer pinned (pre-launch "Notify Me", post-draw "See the
   winners") the hero's "View all prizes" has to sit above it at rest. The
   design has 56px to spare on an 812px stage; a phone webview under a native
   header is often ~720–740px, which puts the button under the footer. On such
   stages the prize reveal scales down from its top edge — the button follows
   it — until the button clears the footer by FIT_GAP, but never below
   FIT_MIN. Returns the scale for the hub's --gq-fit and, for useHeroScroll,
   how far the hero may slide: freely when the button clears the footer at
   rest (it then goes under the sheet after ~175px of scroll, as in the
   prototype), else only until the sheet is FIT_GAP short of the button, so
   the button can still be scrolled out from under the footer. */
const FOOTER_H = 102;      // .gq-sticky
const FIT_GAP = 32;
const FIT_MIN = 0.8;
const REVEAL_LOT_H = 250;  // the reveal Lottie; "View all prizes" overlaps its bottom 14px
const VIEWALL_H = 12;      // …so the button ends 12px under it
const REVEAL_TOP = { pre: 376, live: 340 };
const SHEET_GAP = 72;      // button bottom → sheet top, at rest (any fit)
function revealFit(stageHeight, { pre, footer }) {
  if (!footer) return { fit: 1, lagMax: Infinity };
  const room = stageHeight - FOOTER_H - FIT_GAP - REVEAL_TOP[pre ? 'pre' : 'live'] - VIEWALL_H;
  const want = room / REVEAL_LOT_H;
  return {
    fit: Math.min(1, Math.max(FIT_MIN, +want.toFixed(3))),
    lagMax: want >= FIT_MIN ? Infinity : SHEET_GAP - FIT_GAP,
  };
}

/* live "9 Days 8 Hrs 27 Mins" until launch */
function useCountdown(launch) {
  const [label, setLabel] = useState(() => countdownLabel(launch));
  useEffect(() => {
    setLabel(countdownLabel(launch));
    const id = setInterval(() => setLabel(countdownLabel(launch)), 1000);
    return () => clearInterval(id);
  }, [launch]);
  return label;
}

/* native bridge calls throw outside the app webview; swallow so the page
   stays usable when previewed in a browser */
const native = (fn) => {
  try { fn(); } catch (e) { console.log('NATIVE BRIDGE UNAVAILABLE', e); }
};

/* The hub's entrance (hero drops in, tiles and cards rise) plays whenever
   the hub comes into view: on load, and again when a full-screen page
   (story, pre-registration, rewards) hands back to it — the prototype's
   `.hub.enter`, held for 1.4s. */
const ENTER_MS = 1400;
function useHubEnter(ready, covered) {
  const [enter, setEnter] = useState(false);
  useEffect(() => {
    if (!ready || covered) return undefined;
    setEnter(true);
    const t = setTimeout(() => setEnter(false), ENTER_MS);
    return () => clearTimeout(t);
  }, [ready, covered]);
  return enter;
}

/* retry when the campaign request fails (the loading screen itself is QuestLoader) */
function FailedState({ onRetry }) {
  return (
    <div className="gq-root">
      <div className="gq-state">
        <p>Couldn't load The Golden Quest right now.</p>
        <button type="button" className="gq-retry" onClick={onRetry}>Retry</button>
      </div>
    </div>
  );
}

const NO_SHEET = { kind: null, open: false };

/* The Golden Quest (/goldenQuest), opened from the campaign banner in the app.
   Flow: fetch the campaign → the "How to play" story (first open, when
   enabled) → pre-registration (pre-launch, not yet registered) or the
   go-live interstitial (first open after launch) → any chest celebration
   still due → the hub. From the hub: the Gems / Chests tiles open the
   rewards page, the prize / terms / winners links open bottom sheets,
   "How to play" replays the story and "Claim Now" opens pre-registration.
   Everything comes from the campaign API (see api.js / mockResponse.js). */
export default function GoldenQuest() {
  const [status, setStatus] = useState('loading'); // loading | ready | hidden | failed
  const [fetched, setFetched] = useState(null);    // the campaign once loaded, held until the loading screen hands over
  const [loader, setLoader] = useState('on');      // on | fading | off — the branded loading screen
  const [model, setModel] = useState(null);        // mapped resp.goldenQuest
  const [story, setStory] = useState(null);        // null | 'open' (first open) | 'howto' (from the hub)
  const [prereg, setPrereg] = useState(false);     // pre-registration screen over the hub
  const [golive, setGolive] = useState(false);     // "festival is open" interstitial
  const [rewards, setRewards] = useState(null);    // null | 'gems' | 'chests' (rewards page + tab)
  const [sheet, setSheet] = useState(NO_SHEET);    // { kind: prizes | terms | winners, open }
  const [registered, setRegistered] = useState(false); // pre-registration Gems claimed
  const [notify, setNotify] = useState(false);     // launch reminder set
  const [gemsList, setGemsList] = useState([]);    // the user's Gems (wallet.gems, mapped)
  const gemsRef = useRef(gemsList); gemsRef.current = gemsList; // the current list for openGem
  const [chestsList, setChestsList] = useState([]); // the user's Gold Chests (wallet.chests, mapped)
  const [toast, setToast] = useState({ msg: '', gold: false, show: false });
  const [gemsFlash, setGemsFlash] = useState(false); // Gems tile lights up after the free Gems land
  const [chestsFlash, setChestsFlash] = useState(false); // Chests tile, after the survey's chest lands
  const [surveyState, setSurveyState] = useState(null); // { status: new | part | done, answers, door, chestId }
  const [svScreen, setSvScreen] = useState(null);  // { from: 'hub' | 'rewards' } — the survey page
  const toastTimer = useRef(null);
  const flashTimers = useRef([]);
  const loaderTimer = useRef(null);
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);

  const { zoom, stageHeight } = useStage();
  const pre = !!model && model.phase === 'pre';
  const ended = !!model && model.phase === 'ended';
  const footer = pre || (ended && !!model.draw); // the pinned StickyBar
  const { fit, lagMax } = revealFit(stageHeight, { pre, footer });
  const heroRef = useRef(null);
  const tickRef = useRef(null);
  useHeroScroll(heroRef, tickRef, zoom, lagMax);
  /* fetch the hero's prize reveal while the campaign loads, so it starts with the hub */
  useEffect(() => { prefetchLottie(loadHubPrizes); }, []);
  const countdown = useCountdown(model ? model.launch : undefined);
  /* full-screen pages that replace the hub (not the celebrate / go-live dialogs over it) */
  const pageCovered = !!story || prereg || !!rewards || !!svScreen;
  const enter = useHubEnter(status === 'ready', pageCovered);

  /* the loaded campaign takes the page over: first open → story; live first open → go-live; else the hub */
  const applyModel = useCallback((m) => {
    setModel(m);
    setRegistered(m.registered);
    setNotify(m.notify);
    setGemsList(m.gems);
    setChestsList(m.chests);
    setSurveyState(m.survey ? m.survey.state : null);
    setStory(m.storyEnabled && !m.storySeen ? 'open' : null); // story first on a fresh open
    setGolive(m.phase === 'live' && !m.liveIntroSeen);        // then the go-live interstitial, once
    setPrereg(false);
    setRewards(null);
    setSvScreen(null);
    setSheet(NO_SHEET);
    setStatus('ready');
  }, []);

  const load = useCallback(() => {
    let alive = true;
    setStatus('loading');
    setFetched(null);
    setLoader('on');
    /* Acquire the access token the same way the other campaign pages do, so a
       native deep-link straight here is authenticated before we fetch: capture
       it from the URL and from the native session bridge, then persist it to
       sessionStorage — the single source of truth apiFetch reads. */
    const url = new URL(window.location.href);
    const tokenHeader = url.searchParams.get('token');
    const refreshToken = url.searchParams.get('refreshToken');
    if (tokenHeader) sessionStorage.setItem('AccessToken', tokenHeader);
    if (refreshToken) sessionStorage.setItem('RefreshToken', refreshToken);
    getSessionToken(); // also reads window.__GOSATS_SESSION__ injected by native

    fetchGoldenQuest()
      .then((m) => {
        if (!alive) return;
        if (!m) { setStatus('hidden'); return; }
        /* parked for the loading screen, which hands over once its sequence has played (loaderDone) */
        setFetched(m);
      })
      .catch((err) => {
        console.error('campaign fetch failed:', err);
        if (alive) setStatus('failed');
      });
    return () => { alive = false; };
  }, []);

  useEffect(load, [load]);

  /* the loading screen has played through with the campaign in hand: the page
     takes over underneath (the hub starts its entrance) while it fades out */
  const loaderDone = useCallback(() => {
    if (fetched) applyModel(fetched);
    setLoader('fading');
    clearTimeout(loaderTimer.current);
    loaderTimer.current = setTimeout(() => setLoader('off'), LOADER_EXIT_MS);
  }, [fetched, applyModel]);

  /* The native app can inject the session asynchronously (postMessage) after
     mount; if the first load failed for lack of a token, run it again. */
  useEffect(() => listenForSession(() => { if (statusRef.current === 'failed') load(); }), [load]);

  /* a chest the user hasn't been shown yet → celebrate once the hub is clear */
  const pendingChest = model && model.phase === 'live' && !story && !prereg && !golive && !rewards && !sheet.kind && !svScreen
    ? chestsList.find((c) => !c.celebrated) || null
    : null;

  /* the hub underneath must not scroll while something covers it */
  const overlay = !!story || prereg || golive || !!rewards || !!pendingChest || !!svScreen;
  const covered = overlay || !!sheet.kind;
  useEffect(() => {
    document.body.classList.toggle('gq-locked', covered);
    return () => document.body.classList.remove('gq-locked');
  }, [covered]);

  const showToast = useCallback((msg, gold = false) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, gold, show: true });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2200);
  }, []);
  useEffect(() => () => { clearTimeout(toastTimer.current); clearTimeout(loaderTimer.current); flashTimers.current.forEach(clearTimeout); }, []);

  /* ---- story ---- */
  const openStory = useCallback(() => setStory('howto'), []);
  const endStory = useCallback(() => {
    if (story === 'open') {
      markStorySeen().catch(() => {});
      /* first open before launch without the free Gems → pre-register next */
      if (!registered && model && model.phase === 'pre') setPrereg(true);
    }
    setStory(null);
  }, [story, registered, model]);

  /* ---- pre-registration ---- */
  const openPrereg = useCallback(() => setPrereg(true), []);
  const closePrereg = useCallback(() => setPrereg(false), []);
  /* the claim carries the "Remind me when it starts" toggle as `notify` */
  const claimPrereg = useCallback((remind) => preRegister({ notify: remind }), []);
  /* `res` is the API's answer: the Gems it reserved (with the ids OPEN_GEM
     accepts from launch) and the reminder it stored */
  const donePrereg = useCallback((remind, res) => {
    const launchMs = model ? model.launch.getTime() : Date.now();
    const reserved = res && Array.isArray(res.gems) && res.gems.length
      ? mapGems(res.gems)
      : mapGems(Array.from({ length: model ? model.preRegGems : 0 }, (_, i) => ({ id: `prereg-${i + 1}`, source: 'PRE_REG', earnedAt: launchMs })));
    setRegistered(true);
    setNotify(res && typeof res.reminderSet === 'boolean' ? res.reminderSet : remind);
    /* the reserved Gems appear in the wallet right away; they unlock at launch */
    setGemsList((list) => (list.some((g) => g.source === 'PRE_REG') ? list : reserved.concat(list)));
    setPrereg(false);
    /* once the hub is back: the Gems count bumps, the tile flashes gold and the toast confirms */
    flashTimers.current.forEach(clearTimeout);
    flashTimers.current = [
      setTimeout(() => {
        setGemsFlash(true);
        showToast(`${reserved.length} Gems reserved · they unlock on ${fmtDay(new Date(launchMs))}`, true);
      }, 700),
      setTimeout(() => setGemsFlash(false), 1400),
    ];
  }, [model, showToast]);
  /* a decline carries the backend's reason (e.g. pre-registration has closed) */
  const failPrereg = useCallback((err) => {
    console.error('pre-registration failed:', err);
    showToast(err && err.refused ? err.message : 'Something went wrong, please try again');
  }, [showToast]);

  const toggleNotify = useCallback(() => {
    const on = !notify;
    setNotify(on);
    showToast(on ? "We'll remind you at 12:00 AM on 18 Oct" : 'Reminder removed');
    setReminder(on).catch((err) => {
      console.error('reminder update failed:', err);
      setNotify(!on);
      showToast('Something went wrong, please try again');
    });
  }, [notify, showToast]);

  /* ---- go-live interstitial ---- */
  const doneGolive = useCallback(() => {
    setGolive(false);
    markLiveIntroSeen().catch(() => {});
  }, []);

  /* ---- chest celebration ---- */
  const doneCelebrate = useCallback(() => {
    if (!pendingChest) return;
    const { id } = pendingChest;
    setChestsList((list) => list.map((c) => (c.id === id ? { ...c, celebrated: true } : c)));
    markChestCelebrated(id).catch(() => {});
  }, [pendingChest]);

  /* ---- rewards page ---- */
  const openRewards = useCallback((tab) => setRewards(tab), []);
  const closeRewards = useCallback(() => setRewards(null), []);
  /* the API decides the reward; the wallet updates as soon as it answers and
     the Gem-break stage reveals it (so no toast — it would give the reward away).
     `uid` is the Gem's page key; OPEN_GEM takes its backend `id` (which the
     wallet can repeat), so only the Gem that was tapped is marked open. */
  const openGem = useCallback(async (uid) => {
    const gem = gemsRef.current.find((g) => g.uid === uid);
    const { reward, openedAt: at, alreadyOpened } = await openGemApi(gem ? gem.id : uid);
    if (alreadyOpened) console.warn(`gem ${gem ? gem.id : uid} was already open on the backend; showing what it holds`);
    setGemsList((list) => list.map((g) => (g.uid === uid ? { ...g, opened: { at, reward } } : g)));
    return { reward, at };
  }, []);
  /* a decline carries the backend's reason (e.g. "Gems unlock on 18 Oct") */
  const failOpenGem = useCallback((err) => {
    console.error('open gem failed:', err);
    showToast(err && err.refused ? err.message : "Couldn't open that Gem, please try again");
  }, [showToast]);

  /* ---- bottom sheets ---- */
  const openSheet = useCallback((kind) => setSheet({ kind, open: true }), []);
  const closeSheet = useCallback(() => setSheet((s) => ({ ...s, open: false })), []);
  /* keep the content mounted while it slides out */
  useEffect(() => {
    if (sheet.kind && !sheet.open) {
      const t = setTimeout(() => setSheet(NO_SHEET), SHEET_EXIT_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [sheet]);

  /* ---- the quick survey ----
     "Start survey" / "Resume survey" on the hub card, and the rewards page's
     entry row, open the survey page straight away (no intro sheet). Leaving
     midway saves the answers; finishing credits the Golden Chest and plays
     the claim moment, which hands back to the hub with the Chests tile lit.
     Analytics: "Survey Started" on a fresh start (not on a resume), and
     "Survey QA" { question, answer } for every question submitted. */
  const openSurvey = useCallback((from) => {
    setSvScreen({ from });
    if (surveyState && surveyState.status === 'new') trackSurveyStarted();
  }, [surveyState]);
  const answerSurvey = useCallback((questionId, answer) => { trackSurveyAnswer(questionId, answer); }, []);
  /* back / close on the page: keep what's answered so far */
  const exitSurvey = useCallback((answers, door) => {
    setSvScreen(null);
    if (!Object.keys(answers).length) return;
    /* the answers stay in page state either way; the toast waits for the backend to keep them */
    setSurveyState((s) => ({ ...s, status: 'part', answers, door }));
    const shownAt = Date.now();
    saveSurveyProgress({ answers })
      .then(() => {
        const wait = Math.max(0, 450 - (Date.now() - shownAt));
        flashTimers.current.push(setTimeout(() => showToast('Progress saved — finish anytime', true), wait));
      })
      .catch((err) => console.error('survey progress save failed:', err));
  }, [showToast]);
  /* the last answer: POST { action: 'SURVEY_SUBMIT', answers }; the backend records
     the survey and credits the Golden Chest, which joins the chests list */
  const finishSurvey = useCallback(async (answers, door) => {
    const res = await submitSurveyApi({ answers });
    const chest = surveyChestFrom(res);
    setChestsList((list) => (list.some((c) => c.id === chest.id) ? list : list.concat(chest)));
    setSurveyState((s) => ({ ...s, status: 'done', answers, door, chestId: chest.id }));
    return chest;
  }, []);
  const failSurvey = useCallback((err) => {
    console.error('survey submit failed:', err);
    showToast(err && err.refused ? err.message : "Couldn't submit the survey, please try again");
  }, [showToast]);
  /* "Back to the Quest": the hub, with the Chests tile lighting up for the new
     chest. `won` is the prize when the chest was opened on the spot (claimed
     after the reveal day); otherwise the chest waits for the draw. */
  const homeFromSurvey = useCallback((won = null) => {
    setSvScreen(null);
    setRewards(null);
    flashTimers.current.forEach(clearTimeout);
    flashTimers.current = [
      setTimeout(() => {
        setChestsFlash(true);
        showToast(won ? `${won} won from your survey chest` : `Golden Chest added · opens ${model ? fmtDay(model.grandPrizes.drawOn) : '13 Nov'}`, true);
      }, 500),
      setTimeout(() => setChestsFlash(false), 1200),
    ];
  }, [model, showToast]);

  const copyCode = useCallback(() => {
    const code = model ? model.referralCode : '';
    if (window.navigator && window.navigator.clipboard) {
      window.navigator.clipboard.writeText(code).catch(() => {});
    }
    showToast(`Code ${code} copied`);
  }, [model, showToast]);

  const share = useCallback(() => {
    if (model) native(() => shareWithFriends(model.shareMessage));
  }, [model]);
  const back = useCallback(() => native(goHome), []);

  /* dispatcher for the streak CTA actions the API sends */
  const runAction = useCallback((action) => {
    switch (action) {
      case 'ADD_MONEY': native(loadCard); break;                    // native card recharge
      case 'VOUCHERS': native(offersBrands); break;                 // native Brands screen
      case 'BUY_GOLD': native(() => lumpsumScreen(null, true)); break; // native lumpsum gold purchase
      case 'REFER': share(); break;                                 // native share sheet
      default: break; // unknown actions are no-ops
    }
  }, [share]);

  /* campaign off → nothing to show */
  if (status === 'hidden') return null;
  if (status === 'failed') return <FailedState onRetry={load} />;
  /* the loading screen: first child of the root in both trees below, so the
     same instance carries on fading over the page once it's ready */
  const loaderEl = loader !== 'off'
    ? <QuestLoader key="loader" ready={!!fetched} fading={loader === 'fading'} onDone={loaderDone} zoom={zoom} stageHeight={stageHeight} />
    : null;
  if (status !== 'ready' || !model) return <div className="gq-root">{loaderEl}</div>;

  const { phase, preRegGems, referralCode, streaks, grandPrizes, faqs, terms, draw, storyEnabled, survey } = model;
  const unopened = gemsList.filter((g) => !g.opened).length;
  const { drawOn } = grandPrizes;
  const drawDay = fmtDayLong(drawOn);
  /* the chests are revealed from `grandPrizes.drawOn` (the API's reveal day), or
     once the backend has published the draw — not when the campaign ends: the
     quest closes on 11 Nov and the chests open on 13 Nov. Before that the
     chests stay locked ("Opens 13 Nov"); after it they read Drawn / Won, and a
     survey chest claimed after the reveal can be opened on the spot. */
  const drawn = !!draw || Date.now() >= drawOn.getTime();
  /* the survey's "Bonus rewards" block: while the campaign runs, when the
     backend offers it (or it's done — the card then says so) */
  const showBonus = !!survey && !!surveyState && !ended && (survey.takeable || surveyState.status === 'done');

  let pill;
  if (pre) pill = `Campaign starts on ${fmtDayOrdinal(model.launch)}`;
  else if (ended) pill = `The quest ended · Chests ${drawn ? 'opened' : 'open'} on ${fmtDay(drawOn)}`;
  else pill = `The quest ends ${fmtMonDayOrdinal(model.end)}`;

  let sheetContent = null;
  if (sheet.kind === 'prizes') sheetContent = <PrizesList prizes={grandPrizes} drawOn={drawOn} />;
  else if (sheet.kind === 'terms') sheetContent = <TermsList terms={terms} />;
  else if (sheet.kind === 'winners' && draw) sheetContent = <WinnersList draw={draw} total={grandPrizes.total} />;

  return (
    <div className="gq-root">
      {loaderEl}
      <div className="gq-page" style={{ zoom }} aria-hidden={covered}>
        <div className={`gq-hub${enter ? ' gq-enter' : ''}`} data-phase={phase} style={fit < 1 ? { '--gq-fit': fit } : undefined}>
          <div className="gq-hub-content">
            <Hero
              phase={phase}
              registered={registered}
              preRegGems={preRegGems}
              gems={gemsList.length}
              unopened={unopened}
              chests={chestsList.length}
              countdown={countdown}
              launch={model.launch}
              drawOn={drawOn}
              drawn={drawn}
              heroRef={heroRef}
              covered={pageCovered}
              showHowTo={storyEnabled}
              flash={gemsFlash}
              chestsFlash={chestsFlash}
              onBack={back}
              onShare={share}
              onHowTo={openStory}
              /* before launch the Gems tile is the "Claim 5 Gems" call: it opens pre-registration */
              onGems={pre && !registered ? openPrereg : () => openRewards('gems')}
              onChests={() => openRewards('chests')}
              onViewPrizes={() => openSheet('prizes')}
            />

            <div className="gq-body">
              <TickerBand />
              <div className="gq-upper">
                <div className="gq-mid">
                  <Lantern pos="l5" />
                  <Lantern pos="l6" chain="chain2" />
                  <Lantern pos="l7" mirror />
                  <Lantern pos="l8" chain="chain2" mirror />
                </div>
                <div className="gq-pill"><span>{pill}</span></div>
                <SectionTitle variant="streaks">Your Streaks</SectionTitle>
                <div className="gq-rule">
                  {ended
                    ? 'Streaks are closed. Your chests were part of the draw.'
                    : 'Each streak and Golden chest can only be completed once. Referrals keep earning Gems.'}
                </div>
                <div className="gq-cards">
                  {streaks.map((s) => (
                    <StreakCard
                      key={s.key}
                      streak={s}
                      phase={phase}
                      progress={s.progress}
                      completed={s.completed}
                      extraGems={s.extraGems}
                      onCta={() => runAction(s.action)}
                    />
                  ))}
                </div>

                {showBonus && (
                  <div className="gq-bonus">
                    <SectionTitle variant="bonus-t">Bonus rewards</SectionTitle>
                    <div className="gq-rule">Two minutes on your money habits — claim a free Golden Chest.</div>
                    <SurveyCard survey={survey} state={surveyState} onStart={() => openSurvey('hub')} />
                  </div>
                )}
              </div>

              <GrandPrizes prizes={grandPrizes} onViewAll={() => openSheet('prizes')} />
              <LinksSection faqs={faqs} onTerms={() => openSheet('terms')} />
              <ShareCard code={referralCode} onCopy={copyCode} onShare={share} />
            </div>
          </div>
        </div>

        {/* the ticker, pinned to the top once the body's band scrolls up to it
            (useHeroScroll toggles .gq-show); rendered alongside the band so the
            two marquees run in step and the hand-over is seamless */}
        <TickerBand fixed innerRef={tickRef} />

        {footer && (
          <StickyBar
            phase={phase}
            grandTotal={grandPrizes.total}
            launch={model.launch}
            drawOn={drawOn}
            notify={notify}
            onToggle={toggleNotify}
            onWinners={() => openSheet('winners')}
          />
        )}
      </div>

      {/* bottom sheets: outside the (aria-hidden) page, fixed over the hub */}
      {sheet.kind && sheetContent && (
        <div style={{ zoom }}>
          <Sheet open={sheet.open} label={sheet.kind === 'prizes' ? 'Golden Chest Prizes' : (sheet.kind === 'terms' ? 'Terms & Conditions' : 'Grand draw results')} stageHeight={stageHeight} onClose={closeSheet}>
            {sheetContent}
          </Sheet>
        </div>
      )}

      {rewards && !story && (
        <Rewards
          phase={phase}
          tab={rewards}
          onTab={setRewards}
          gems={gemsList}
          chests={chestsList}
          grandTotal={grandPrizes.total}
          drawDay={drawDay}
          launch={model.launch}
          drawOn={drawOn}
          drawn={drawn}
          survey={survey}
          surveyState={surveyState}
          onSurvey={() => openSurvey('rewards')}
          zoom={zoom}
          stageHeight={stageHeight}
          onBack={closeRewards}
          onOpen={openGem}
          onError={failOpenGem}
        />
      )}

      {svScreen && survey && surveyState && !story && (
        <Survey
          survey={survey}
          initial={surveyState}
          drawOn={drawOn}
          locked={!drawn}
          zoom={zoom}
          stageHeight={stageHeight}
          onExit={exitSurvey}
          onAnswer={answerSurvey}
          onSubmit={finishSurvey}
          onError={failSurvey}
          onHome={homeFromSurvey}
          onToast={showToast}
        />
      )}

      {pendingChest && (
        <Celebrate
          chest={pendingChest}
          chestCount={chestsList.length}
          drawDay={drawDay}
          zoom={zoom}
          stageHeight={stageHeight}
          onDone={doneCelebrate}
        />
      )}

      {golive && !story && (
        <GoLive
          registered={registered}
          unopened={unopened}
          endDay={fmtDayLong(model.end)}
          zoom={zoom}
          stageHeight={stageHeight}
          onDone={doneGolive}
        />
      )}

      {prereg && !story && (
        <PreReg
          gems={preRegGems}
          remind
          zoom={zoom}
          stageHeight={stageHeight}
          onBack={closePrereg}
          onClaim={claimPrereg}
          onDone={donePrereg}
          onError={failPrereg}
        />
      )}

      {story && (
        <Story
          drawLabel={fmtMonDayOrdinal(drawOn)}
          startsLabel={pre ? `Starts on ${fmtDayOrdinal(model.launch)}` : ''}
          zoom={zoom}
          stageHeight={stageHeight}
          onEnd={endStory}
        />
      )}

      {/* outside the (aria-hidden) page so it shows above, and is announced over, the overlays */}
      <div style={{ zoom }}>
        <Toast toast={toast} />
      </div>
    </div>
  );
}
